<script setup>
import { computed } from "vue"

const apiBase = useApiBase()
const apiUrl = apiBase ? `${apiBase}/api/features/` : "/api/features/"
const { data } = await useFetch(apiUrl, { key: "home-features" })

const resolveImageUrl = (url) => {
  if (!url) return ""
  if (/^https?:\/\//i.test(url)) return url
  if (url.startsWith("/media/")) return url
  return url
}

const buildSrcset = (srcset) => {
  if (!srcset || !Array.isArray(srcset) || srcset.length === 0) return ""
  return srcset
    .map((item) => `${resolveImageUrl(item.url)} ${item.w}w`)
    .join(", ")
}

const sectionData = computed(() => data.value?.section || null)
const itemsData = computed(() => (data.value?.items?.length ? data.value.items : []))

const openItemId = ref(null)

const toggleItem = (item) => {
  const id = item?.id ?? item?.title
  if (!id) return
  openItemId.value = openItemId.value === id ? null : id
}
</script>

<template>
  <section v-if="sectionData" class="section-features">
    <div
      class="section__title"
      style="translate: none; rotate: none; scale: none; opacity: 1; transform: translate(0px, 0%);"
    >
      <div
        class="title__text__wrapper who-title-width"
        style="translate: none; rotate: none; scale: none; opacity: 1; transform: translate(0px, 0%);"
      >
        <div class="section__tag">
          <p class="text-regular-16 color-text-dark">{{ sectionData.tag_label }}</p>
          <div class="icon-14">
            <svg xmlns="http://www.w3.org/2000/svg" width="100%" viewBox="0 0 14 14" fill="none">
              <path
                d="M11.813 3.9375H2.18799C1.94636 3.9375 1.75049 4.13338 1.75049 4.375V11.375C1.75049 11.6166 1.94636 11.8125 2.18799 11.8125H11.813C12.0546 11.8125 12.2505 11.6166 12.2505 11.375V4.375C12.2505 4.13338 12.0546 3.9375 11.813 3.9375Z"
                stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
              ></path>
              <path
                d="M9.1875 3.9375V3.0625C9.1875 2.83044 9.09531 2.60788 8.93122 2.44378C8.76712 2.27969 8.54456 2.1875 8.3125 2.1875H5.6875C5.45544 2.1875 5.23288 2.27969 5.06878 2.44378C4.90469 2.60788 4.8125 2.83044 4.8125 3.0625V3.9375"
                stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
              ></path>
              <path
                d="M12.2503 6.90753C10.6547 7.83069 8.84331 8.3154 6.99989 8.3125C5.15678 8.3154 3.34568 7.83085 1.75024 6.90799"
                stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
              ></path>
              <path d="M6.34375 6.5625H7.65625" stroke="currentColor" stroke-linecap="round"
                    stroke-linejoin="round"></path>
            </svg>
          </div>
        </div>
        <h3 class="h3-40 width-features-mb">
          {{ sectionData.title_prefix }}
          <span class="color-span-scorpion">{{ sectionData.title_suffix }}</span>
        </h3>
      </div>
      <div
        class="title__description__wrapper width-588"
        style="translate: none; rotate: none; scale: none; opacity: 1; transform: translate(0px, 0%);"
      >
        <div class="text-regular-16" v-html="sectionData.description"></div>
      </div>
    </div>
    <div class="features__wrapper">
      <div
        class="features__left-block"
        style="translate: none; rotate: none; scale: none; transform: translate(0px, 0px); opacity: 1;"
      >
        <img
          v-if="sectionData.image_url"
          :src="resolveImageUrl(sectionData.image_url)"
          :srcset="buildSrcset(sectionData.image_srcset)"
          sizes="(max-width: 479px) 93vw, (max-width: 767px) 73vw, (max-width: 991px) 79vw, 80vw"
          height="Auto"
          :alt="sectionData.image_alt || 'features-img'"
          class="image-cover uniq-img"
        >
      </div>
      <div class="features__right-block">
        <div
          v-for="(item, index) in itemsData"
          :key="item.id || item.title"
          data-accordion-item
          class="accordion-item"
          :class="{ 'is--active': openItemId === (item.id ?? item.title) }"
          style="translate: none; rotate: none; scale: none; transform: translate(0px, 0px); opacity: 1;"
          role="button"
          tabindex="0"
          @click="toggleItem(item)"
          @keydown.enter.prevent="toggleItem(item)"
          @keydown.space.prevent="toggleItem(item)"
        >
          <div data-question class="question-block">
            <span class="feature-number">{{ String(index + 1).padStart(2, "0") }}</span>
            <h5>{{ item.title }}</h5>
            <div class="question-arrow">
              {{ openItemId === (item.id ?? item.title) ? "−" : "+" }}
            </div>
          </div>
          <div class="answer-block"><div class="text-regular-16" v-html="item.description"></div></div>
        </div>
      </div>
    </div>
    <div id="features" class="scroll-anchor"></div>
  </section>
</template>

<style lang="scss" scoped>
@use '/assets/scss/sections/_features.scss';
</style>
